/**
  * @class mmodModule
  * @extends msysListener
  * @description
  *
  * # Purpose
  * All GUI modules shall inherit from this base class.
  * The mmmodModule creates HTML <m-window> element for hosting modules's content
  * and communicates with msysWM (Window Manager) to keep window in the proper position/size/visibility.
  *
  * # Use
  * This class is not intended for direct instatiation.
  * You can create new Mosy Module automatically using npm script:
  * ```
  * npm run create <YourModuleName> mmodModule [msg1,msg2,msg3...]
  * - <YourModuleName> shall have mmod prefix
  * - msg1,... are names of messages you plan to handle in your module; generator will prepare skeletons within generated code for them
  * ```
  *
  * Alternatively you can inherit from the mmodModule using code like this:
  * ```
  * import mmodModule from 'mmodModule'
  * export default class <YourModuleName> extends mmodModule {
  *   constructor(id, cfg, add_cfg_schema, add_dependencies) {
  *     var dependencies = [...]; // module depends on these additional listeners
  *     var cfg_schema = {
  *      ... module specific JSON schema for configuration
  *     };
  *     super(id, cfg, [cfg_schema,add_cfg_schema], [dependencies, add_dependencies]); // call mmodModule constructor
  *   } // constructor
  *
  *   mmStart(sender, action, params) {
  *     // this will be called when all dependencies are finished starting (either started or exited)
  *     started(); // indicate to others we have started; alternatively use exited() for unsuccessful start
  *   }
  * }
  * ```
  *
  * # Implementation notes
  * Implemented as derived class from msysListener, which provides methods for messaging communication.
  *
  */

import msysListener from 'msysListener'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'

const moment = require('moment')
const mfs = require('msysFS')
const geo = require('msysGeo')
const MAX_SEARCH_RESULTS = 30
const { base64ToBytes } = require('../../../src/res/js/base64.js')
const PDFObject = require('pdfobject')

export default class mmodModule extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class
  * mmodModule is abstract class and shall never be instantiated directly, but used for inheritance only.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysWM', 'mmodSplash'], dependencies)
    cfg_schema = _.extend({
      order: { type: 'number', default: 0, description: 'Order of the module in ATB.' },
      screen: { type: 'number', default: 0, description: 'Index of screen where the window shall be placed' },
      init: { type: 'object', default: { type: 'none' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      keyboard: { type: 'boolean', default: false, description: 'Whether to show keyboard when input is focused' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this._locations = {}
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmStart(sender, params) {
    if(this.config.disabled) return super.mmStart(sender||{ listener_id: this.listener_id }, params||{})
    this.users = {}
    if(!this.el) {
      this.publish('msysWM', 'mmWindowSet', this.config.init)
    }else{
      super.mmStart(sender||{ listener_id: this.listener_id }, params||{})
      this.subscribe('#USERS')
    }
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Window manager informs module by this message that it should change its window position/size.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.type - type of window
  * @param {integer} params.screen - index of screen to which window belongs to
  * @param {string} params.orientation - orientation of the screen to which window belongs to
  * @param {object} params.doc - reference to document root DOM object
  * @param {boolean} [params.focused] - true if window is focused
  * @param {integer} params.x - position of left edge
  * @param {integer} params.y - position of top edge
  * @param {integer} params.z - z-order
  * @param {integer} params.width - width of window
  * @param {integer} params.height - height of window
  */
  mmWindow(sender, params) {
    if(!this.el) {
      this.el = params.doc.body.appendChildren({
        tag: 'mele-window',
        mod: this.listener_id,
        module: this,
        publishToModule: (action, params)=>{
          this.publish('#ME', action, params)
        },
        publishTo: (target, action, params)=>{
          this.publish(target, action, params)
        },
        publishOpen: (target, id)=>{
          this.publish(target, 'mmOpen', { id })
          this.publish('msysWM', 'mmWindowFocus', { id: target })
        },
      })

      // call again mmWindow to perform usual window handling tasks
      this.mmWindow(sender, params)

      // when top-level element is created, call initWindow method that shall create other GUI elements
      if(typeof (this.initWindow)==='function') {
        this.initWindow()
      }else{
        this.warn('Missing initWindow for module '+this.listener_id)
        this.startWhenElementsReady()
      }
      return
    }

    this.window_active = !!params.focused
    params.focused = !!params.focused
    this.el.updateProperties(_.pick(params, ['x', 'y', 'width', 'height', 'z', 'focused']))
    if(this.el._elModal) {
      setTimeout(()=>{
        this.el._elModal.reposition()
      }, 10)
    }
  } // mmWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method that updates module's elWindow with given JSON definition
  * useful for putting performance marks on one place
  */
  loadForm(form) {
    this.el.updateProperties(form)
  }

  setPrintTimestamp() {
    if(this.el && this.el._printheader) {
      this.el._printheader.label = '{{Generated}}: {{user.fullname.'+this.user_id+'}}, '+moment().format(this.el.translate('format.datetime'))
    }
  }

  clearPrintTimestamp() {
    if(this.el && this.el._printheader) {
      this.el._printheader.label = ''
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method that waits until this.el element and all its subelements are ready and then invoke the callback
  */
  whenElementsReady(callback) {
    let dt = Date.now()
    var waitForReady = setInterval(()=>{
      if(this.el._ready) {
        clearInterval(waitForReady)
        callback()
      }else if(dt && Date.now()-dt>100) {
        dt = 0
        this.warn('LONG WAIT FOR MODULE ELEMENTS')
      }
    }, 1)
  } // whenElementsReady

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method that calls mmStart() and then callback() (if provided) when this.el and subelements are ready
  */
  startWhenElementsReady(callback) {
    this.whenElementsReady(()=>{
      this.mmStart()
      if(typeof (callback)==='function') { callback() }
    })
  } // startWhenElementsReady

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to set language
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.lang] - desired language in 2 character ISO 639-1
  */
  mmSetLanguage(sender, params) {
    this.el.language = params.lang
  } // mmSetLanguage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application behavior
  * @param {object} sender - Sender of the message
  * @param {object} params - message parameters
  * @param {string} [params.behavior] - desired behavior
  */
  mmSetBehavior(sender, params) {
    this.behavior = params.behavior || this.behavior || 'tablet'
    const device = mfs.device()
    const outcoming_behavior = device==='phone' ? 'phone' : this.behavior
    if(this.el.behavior !== outcoming_behavior) {
      this.el.behavior = outcoming_behavior
    }
    return outcoming_behavior
  } // mmSetBehavior

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application color theme
  * @param {object} sender - Sender of the message
  * @param {object} params - message parameters
  * @param {string} [params.theme] - desired color theme
  */
  mmSetTheme(sender, params) {
    if(this.el.theme !== params.theme) {
      this.el.theme = params.theme
      this.el.forAll('.mele-map', el=>{
        if(_.toString(el.base)==='') el.baseChanged()
      })
    }
  } // mmSetTheme

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    this.login = _.toString(params.login)
    this.user_id = _.toString(params.user_id)
    this.callsign = _.toString(params.callsign)
    this.user_roles = params.roles
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method to log out current user
  */
  logoutUser() {
    this.publish('mmodNotifications', 'mmNotifyClose', {})
    this.publish('msysUser', 'mmUserLogin', { login: '' })
  } // logoutUser

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    this.user_status = params.status
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies watchers that some system users were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - whether all fields or just subset is sent
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} params.version - version of data sent by server
  * @param {array} params.list - array of changed records
  */
  mmUsers(sender, params) {
    if(params.full) this.users = {}
    params.list.forEach(change=>{ this.users[change.id] = change })
  } // mmUsers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysUser informs others on changed roles of currently logged user
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {array} [params.roles] - list of roles assigned to user (in any group)
  */
  mmUserRoles(sender, params) {
    this.user_roles = params.roles || []
    if(this.el) { this.el.setAttribute('roles', params.roles.join(' ')) }
  } // mmUserRoles

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Special helper method to handle response to modification request
  * @param {array} changes - list of changes
  */
  defaultReplyHandling(changes, elList, elDetail) {
    changes = _.toArray(changes)
    return({
      mmSuccess: (sender, params)=>{
        let errors = ''
        const results = _.toArray(params.results)
        for(let i=0; i<changes.length; i++) {
          const change = changes[i]
          const delete_id = _.toString(change.delete)
          const old_id = delete_id || _.toString(change.id)
          const [error, new_id] = _.toArray(results[i])
          if(error) {
            // error
            errors += error+'<br/>'
          }else {
            if(elDetail && elDetail.datakey===old_id) {
              elDetail.commitValues()
            }
            if(elList) {
              if(delete_id) {
                elList.delete(delete_id)
              }else if(!_.isNil(new_id)) {
                elList.changeItemKey(old_id, new_id)
              }
            }
          }
        } // for each change

        this.modalError(errors, 'Error')
      },
      mmFailure: (sender, params)=>{
        this.modalError(params.error, 'Error')
      },
      mmUndelivered: ()=>{
        this.modalError('Server unavailable', 'Error')
      },
    })
  } // defaultReplyHandling

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open module's modal view
  */
  modalOpen(json) {
    return this.el ? this.el.modalOpen(json) : null
  } // modalOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Find module's modal view/window
  */
  modalFind(key) {
    return this.el ? this.el.modalFind(key) : null
  } // modalFind

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Finds default modal view/window
  */
  modalFindDefault() {
    return this.el ? this.el.modalFindDefault() : null
  } // modalFindDefault

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Close module's modal view/window
  */
  modalClose(key) {
    return this.el ? this.el.modalClose(key) : null
  } // modalClose

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Close module's default modal view/window
  */
  modalCloseDefault() {
    return this.el ? this.el.modalCloseDefault() : null
  } // modalCloseDefault

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open/close module's modal window
  */
  modalWindow(json) {
    return this.el ? this.el.modalWindow(json) : null
  } // modalWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open confirmation modal window
  */
  modalConfirm(content, dialog_title, yes_callback) {
    if(this.el) return this.el.modalConfirm(content, dialog_title, yes_callback)
  } // modalConfirm

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open wait modal window
  */
  modalWait(wait_message, dialog_title) {
    if(this.el) return this.el.modalWait(wait_message, dialog_title)
  } // modalWait

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open info modal window
  */
  modalInfo(message, title) {
    if(this.el) return this.el.modalInfo(message, title)
  } // modalInfo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open error modal window
  */
  modalError(message, title) {
    if(this.el) return this.el.modalError(message, title)
  } // modalError

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open prompt modal window
  */
  modalPrompt(content, dialog_title, yes_callback) {
    if(this.el) return this.el.modalPrompt(content, dialog_title, yes_callback)
  } // modalPrompt

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open module's popup view (modal only for that module)
  */
  popupOpen(json) {
    return this.el ? this.el.popupOpen(json) : null
  } // popupOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Find module's popup view/window (modal only for that module)
  */
  popupFind(key) {
    return this.el ? this.el.popupFind(key) : null
  } // popupFind

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Close module's popup view/window (modal only for that module)
  */
  popupClose(key) {
    return this.el ? this.el.popupClose(key) : null
  } // popupClose

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Close module's default popup view/window (modal only for that module)
  */
  popupCloseDefault() {
    return this.el ? this.el.popupCloseDefault() : null
  } // popupCloseDefault

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open confirmation popup window (modal only for that module)
  */
  popupConfirm(content, dialog_title, yes_callback) {
    return this.el ? this.el.popupConfirm(content, dialog_title, yes_callback) : null
  } // popupConfirm

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open wait popup window (modal only for that module)
  */
  popupWait(message, title) {
    return this.el ? this.el.popupWait(message, title) : null
  } // popupWait

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open info popup window (modal only for that module)
  */
  popupInfo(message, title) {
    return this.el ? this.el.popupInfo(message, title) : null
  } // popupInfo

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Open error popup window (modal only for that module)
  */
  popupError(message, title) {
    return this.el ? this.el.popupError(message, title) : null
  } // popupError

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens new browser window with given content
*/
  print(html) {
    if(!this.el) return
    html = this.el.translate(html)

    const body = this.el.ownerDocument.body
    body.setAttribute('print', '')
    const elPrinter = body.find('>div.print') || body.appendChildren({ tag: 'div', class: 'print' })
    const elContent = elPrinter.appendChildren({ key: 'print.content', tag: 'div' })
    elContent.innerHTML = html
    const elCloser = elPrinter.appendChildren({ key: 'print.close', tag: 'mele-button', type: 'iconmini', iconleft: 'sys.close', label: 'form.close', color: 'orange' })
    elCloser.eventButtonClicked = ()=>{
      body.removeAttribute('print')
      elPrinter.deleteElement()
    }

    setTimeout(()=>{
      this.el.ownerDocument.defaultView.print()
    }, 1000)
  } // print

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * initiate browser download of provided file in form of Base64 data
*/
  createDownloadFromBase64(mime_type, file_name, base64) {
    if(!this.el) return
    let doc = this.el.ownerDocument
    const blob = _.base64toBlob(base64, mime_type)
    doc = this.el.ownerDocument
    const el = window.el = this.el.createElementFast('a')
    el.style.display = 'none'
    el.download = file_name
    el.href = URL.createObjectURL(blob)
    doc.body.appendChild(el)
    el.click()
    URL.revokeObjectURL(blob)
    doc.body.removeChild(el)
  } // createDownloadFromBase64

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * change of window URL hash
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.hash - part of URL hash parametrizing the request
  */
  mmUrlHash(sender, params) {
    const [id] = params.hash.split('/')
    if(id==='') return
    if(this.elList && this.elList.urlhash==='/') this.elList.selected = id
  } // mmUrlHash

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by msvcSkyPhone when HW button is pressed
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.key - key attribute of the button
  */
  mmHWButton(sender, params) {
    if(!this.el.focused && this.el._viewback.hidden) {
      return
    }

    this.el.eventViewBack(this.el.active)
  } // mmHWButton

  async remotePrint(print_template = 'default', values, view) {
    if(view) {
      values = view.getVisibleValues()
      view.hidden = true
    }
    if(!values) return
    try{
      const req_params = { template: print_template, record: values, lang: this.el.language }
      const { params } = await this.request(':msvrPrint', 'mmGetPrint', req_params, 'mmPrint')
      const blob = base64ToBytes(params.pdfUri)
      this.printUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      if(view) {
        view.hidden = false
        PDFObject.embed(this.printUrl, this.elDetail._body)
      } else {
        const win = window.open(this.printUrl, '_blank')
        win.focus()
      }
    }catch(e) {
      this.modalError(`Error downloading ${print_template}.pdf`, 'Print preview')
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Retrieve available mounts from mBox
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.key - key attribute of the button
  */
  async getMounts(modal_title = 'export.modal.title') {
    // prepare regular expressions for mounts and mounts_exclude
    let re = /.*/
    let non = /^$/
    try{
      if(this.config.mounts) {
        re = new RegExp(this.config.mounts)
      }
      if(this.config.mounts_exclude) {
        non = new RegExp(this.config.mounts_exclude)
      }
    }catch(e) {
      this.error('Invalid mounts configuration')
      if(modal_title !== null && modal_title !== undefined) {
        await this.modalError('export.modal.mounts_misconfiguration', modal_title)
      }
      return
    }

    const modal = this.modalWait('{{Please wait}}...', modal_title)
    modal.classList.add('modal_mounts_wait')
    try{
      const { params: { fs } } = await this.request('SERVER@msysDM', 'mmGetSysInfo', { keys: ['fsSize'] }, 'mmSysInfo')
      modal.modalClose()
      const mounts = fs.map(disk=>disk.mount).filter(mount=>re.test(mount) && !non.test(mount))
      const extras = Array.isArray(this.config.mounts_extra) ? this.config.mounts_extra : []
      for(const extra of extras) {
        if(typeof extra==='string' && extra!=='' && !mounts.includes(extra)) mounts.push(extra)
      }
      if(mounts.length === 0) {
        if(modal_title !== null && modal_title !== undefined) {
          await this.modalError('export.modal.mounts_none', modal_title)
        }
        return []
      }else {
        return mounts
      }
    }catch(e) {
      modal.modalClose()
      this.error(e)
      if(modal_title !== null && modal_title !== undefined) {
        await this.modalError('export.modal.mounts_unavailable', modal_title)
      }
    }
  } // getMounts

  async modalSelectMount(modal_title = 'export.modal.title') {
    const mounts = await this.getMounts(modal_title)
    if(!mounts || mounts.length === 0) return

    // eslint-disable-next-line no-template-curly-in-string
    const content = [{ tag: 'mele-input', key: 'destination', type: 'list', label: 'export.modal.select_usb', itemlabel: '${key}', options: mounts }]
    const values = await this.modalPrompt(content, modal_title)
    const destination = values?.destination
    return destination
  } // modalSelectMount
} // mmodModule
